<script>
export default {
    props: {
        modelValue: [String, Number],
        loadOptions: {
            type: Function,
            required: true,
        },
        onChange: {
            type: Function,
            default: null
        },
        onDisabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            required: false
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            observer: null,
            options: [],
            page: 1,
            search: '',
            hasNextPage: true,
            open: false,
            loadingCount: 0,
            debounceTimer: null,
            requestId: 0
        }
    },

    computed: {
        value: {
            get() {
                const exists = this.options.find(o => o.id == String(this.modelValue))

                if (exists)
                    return String(this.modelValue)

                return null
            },
            set(val) {
                this.$emit('update:modelValue', val)
            }
        },
        loading() {
            return this.loadingCount > 0
        }
    },

    mounted() {
        this.observer = new IntersectionObserver(this.infiniteScroll)
    },

    watch: {
        modelValue: {
            immediate: true,
            async handler(val) {
                if (!val) return

                const exists = this.options.find(o => o.id == val)

                if (!exists) {
                    await this.fetchOptions(true, val)
                }
            }
        },
        value: {
            async handler(val, oldVal) {
                var vm = this;

                if (val === oldVal) return

                if (vm.onChange) {
                    const exists = this.options.find(o => o.id == val)

                    var model = {};
                    if (exists)
                        model = exists.model

                    this.onChange(val, oldVal, model);
                }
            }
        },
        async open(val) {
            if (val) {
                await this.$nextTick()

                if (this.$refs.load instanceof Element) {
                    this.observer.observe(this.$refs.load)
                }
            } else {
                this.observer.disconnect()
            }
        }
    },

    methods: {
        async fetchOptions(reset = false, id = null, customPage = null) {
            this.loadingCount++

            const currentRequestId = ++this.requestId

            try {
                var params = {
                    keyword: this.search,
                    page: customPage ?? this.page,
                    include_id: id
                };

                const resp = await this.loadOptions(params)

                if (currentRequestId !== this.requestId) return

                var options = this.$_.map(resp.options, i => ({ ...i, id: String(i.id) }))

                if (reset) {
                    this.options = options
                } else {
                    this.options = [...this.options, ...options]
                }

                this.hasNextPage = resp.more
            } catch (err) {
                console.error(err)
            } finally {
                this.loadingCount--
            }
        },

        onSearch(search) {
            if (this.observer)
                this.observer.disconnect()

            if (!this.open && !search) return

            this.search = search
            this.page = 1
            this.hasNextPage = true

            this.requestId++

            clearTimeout(this.debounceTimer)

            this.debounceTimer = setTimeout(() => {
                this.fetchOptions(true)
            }, 500)
        },

        async onOpen() {
            this.open = true;
            this.search = '';
            this.page = 1

            const id = this.modelValue ?? null

            await this.fetchOptions(true, id)

            if (this.hasNextPage) {
                await this.$nextTick()
            }
        },

        onClose() {
            if (this.observer)
                this.observer.disconnect()

            this.open = false
        },

        async infiniteScroll([{ isIntersecting, target }]) {
            if (!this.open) return

            if (this.search && this.page === 1) return

            if (isIntersecting && this.hasNextPage && !this.loading) {
                const ul = target.offsetParent
                const scrollTop = ul.scrollTop

                const nextPage = this.page + 1

                await this.fetchOptions(false, null, nextPage)

                this.page = nextPage

                await this.$nextTick()
                ul.scrollTop = scrollTop
            }
        }
    },
}
</script>

<template>
    <v-select v-model="value" :options="options" :filterable="false" label="text" @open="onOpen" @close="onClose"
        :disabled="onDisabled" class="flex-grow-1" @search="onSearch" :reduce="option => option.id"
        :loading="loading && !open" :placeholder="placeholder">
        <template #list-footer>
            <li v-show="loading && open" class="loader">{{ $t('basic.selectbox.loading') }}</li>
            <li v-show="hasNextPage && !loading && open" ref="load" class="loader">
                {{ $t('basic.selectbox.more') }}
            </li>
        </template>
        <template #spinner="{ loading }">
            <div v-if="loading" style="border-left-color: #4b38b3" class="vs__spinner"></div>
        </template>
    </v-select>
</template>

<style scoped>
.loader {
    text-align: center;
    color: #bbb;
}

:deep(.vs__search),
:deep(.vs__selected) {
    font-size: inherit;
    padding-top: 0.25rem;
    padding-right: 0.6rem;
    padding-bottom: 0.25rem;
    padding-left: 0.6rem;
}

:deep(.vs__search::placeholder) {
    color: #bbb;
    opacity: 1;
}
</style>